/**
 * BACKFILL SHIPPING (Admin Tool)
 *
 * One-off migration: scans user docs, reads legacy free-text `location`
 * and `shipsTo` fields, guesses the structured values (shipsFromCountry,
 * currency, shipsToWorldwide, shipsToCountries), and presents a
 * per-user preview so the admin can review before committing.
 *
 * Design decisions:
 *   - No auto-commit. Every row starts with commit=on but the admin
 *     can uncheck any low-confidence guess before saving.
 *   - Never OVERWRITES structured values that are already set (a user
 *     who saved via new Settings page keeps their structured values).
 *   - Free-text mirrors are preserved on save — they're populated
 *     alongside the structured values by the normal Settings save
 *     path, so this backfill leaves them alone.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { SUPPORTED_COUNTRIES, defaultCurrencyForCountry } from '../constants/countries';

const BATCH_LIMIT = 450;

interface Guess {
  uid: string;
  displayName: string;
  legacyLocation: string;
  legacyShipsTo: string;
  currentStructured: {
    shipsFromCountry?: string;
    shipsToWorldwide?: boolean;
    shipsToCountriesCount?: number;
    currency?: string;
  };
  guessed: {
    shipsFromCountry?: string;
    currency?: string;
    shipsToWorldwide?: boolean;
    shipsToCountries?: string[];
  };
  /** Confidence 0-3: 0 = don't apply, 1 = weak, 2 = medium, 3 = strong.
   *  UI defaults commit=on only for medium+. */
  confidence: number;
  commit: boolean;
}

/** Country-name keyword table used by BOTH the ships-from location
 *  guess AND the ships-to parser. Order matters — longer names before
 *  their shorter aliases so "United Kingdom" wins over "United". */
const COUNTRY_KEYWORDS: { code: string; patterns: RegExp[] }[] = [
  { code: 'US', patterns: [/\busa\b/i, /\bunited states\b/i, /\bu\.s\.?a?\.?\b/i, /\bamerica\b/i] },
  { code: 'CA', patterns: [/\bcanada\b/i, /\bcanadian\b/i] },
  { code: 'GB', patterns: [/\bunited kingdom\b/i, /\bengland\b/i, /\bbritain\b/i, /\bu\.?k\.?\b/i, /\bscotland\b/i, /\bwales\b/i] },
  { code: 'IE', patterns: [/\bireland\b/i, /\birish\b/i] },
  { code: 'FR', patterns: [/\bfrance\b/i] },
  { code: 'DE', patterns: [/\bgermany\b/i, /\bdeutschland\b/i] },
  { code: 'IT', patterns: [/\bitaly\b/i, /\bitalia\b/i] },
  { code: 'ES', patterns: [/\bspain\b/i, /\bespaña\b/i] },
  { code: 'PT', patterns: [/\bportugal\b/i] },
  { code: 'NL', patterns: [/\bnetherlands\b/i, /\bholland\b/i] },
  { code: 'BE', patterns: [/\bbelgium\b/i] },
  { code: 'AT', patterns: [/\baustria\b/i] },
  { code: 'CH', patterns: [/\bswitzerland\b/i, /\bswiss\b/i] },
  { code: 'PL', patterns: [/\bpoland\b/i] },
  { code: 'CZ', patterns: [/\bczech\b/i] },
  { code: 'RO', patterns: [/\bromania\b/i] },
  { code: 'GR', patterns: [/\bgreece\b/i] },
  { code: 'TR', patterns: [/\bturkey\b/i, /\btürkiye\b/i, /\bturkish\b/i] },
  { code: 'SE', patterns: [/\bsweden\b/i, /\bswedish\b/i] },
  { code: 'NO', patterns: [/\bnorway\b/i, /\bnorwegian\b/i] },
  { code: 'FI', patterns: [/\bfinland\b/i, /\bfinnish\b/i] },
  { code: 'DK', patterns: [/\bdenmark\b/i, /\bdanish\b/i] },
  { code: 'HU', patterns: [/\bhungary\b/i] },
  { code: 'AU', patterns: [/\baustralia\b/i, /\baustralian\b/i, /\baus\b/i] },
  { code: 'NZ', patterns: [/\bnew zealand\b/i] },
  { code: 'JP', patterns: [/\bjapan\b/i] },
  { code: 'KR', patterns: [/\bkorea\b/i] },
  { code: 'SG', patterns: [/\bsingapore\b/i] },
  { code: 'HK', patterns: [/\bhong kong\b/i] },
  { code: 'PH', patterns: [/\bphilippines\b/i] },
  { code: 'BR', patterns: [/\bbrazil\b/i, /\bbrasil\b/i] },
  { code: 'MX', patterns: [/\bmexico\b/i, /\bméxico\b/i] },
  { code: 'ZA', patterns: [/\bsouth africa\b/i] },
  { code: 'IL', patterns: [/\bisrael\b/i] },
  { code: 'AE', patterns: [/\buae\b/i, /\bemirates\b/i, /\bdubai\b/i] },
];

/** US state abbreviations — if the free text has one of these WITHOUT
 *  a country keyword, we still guess US (medium confidence). */
const US_STATE_HINTS = /\b(al|ak|az|ar|ca|co|ct|de|fl|ga|hi|id|il|in|ia|ks|ky|la|me|md|ma|mi|mn|ms|mo|mt|ne|nv|nh|nj|nm|ny|nc|nd|oh|ok|or|pa|ri|sc|sd|tn|tx|ut|vt|va|wa|wv|wi|wy)\b/i;

function guessShipsFromCountry(location: string): { code?: string; confidence: number } {
  if (!location) return { confidence: 0 };
  for (const { code, patterns } of COUNTRY_KEYWORDS) {
    if (patterns.some((p) => p.test(location))) return { code, confidence: 3 };
  }
  if (US_STATE_HINTS.test(location)) return { code: 'US', confidence: 2 };
  return { confidence: 0 };
}

const WORLDWIDE_PATTERNS = [
  /\bworld ?wide\b/i,
  /\binternational\b/i,
  /\banywhere\b/i,
  /\beverywhere\b/i,
  /\bany country\b/i,
  /\ball countries\b/i,
  /\bglobal\b/i,
];

function guessShipsTo(shipsTo: string, homeCountry?: string): {
  worldwide: boolean;
  countries: string[];
  confidence: number;
} {
  if (!shipsTo) return { worldwide: false, countries: [], confidence: 0 };
  if (WORLDWIDE_PATTERNS.some((p) => p.test(shipsTo))) {
    return { worldwide: true, countries: [], confidence: 3 };
  }
  const found = new Set<string>();
  for (const { code, patterns } of COUNTRY_KEYWORDS) {
    if (patterns.some((p) => p.test(shipsTo))) found.add(code);
  }
  // "US only" pattern — if home country matches and free-text mentions only itself.
  if (found.size === 0 && homeCountry) {
    // e.g. "US only", "domestic only"
    if (/only|domestic/i.test(shipsTo)) {
      found.add(homeCountry);
      return { worldwide: false, countries: [...found], confidence: 2 };
    }
  }
  if (found.size > 0) return { worldwide: false, countries: [...found], confidence: 3 };
  return { worldwide: false, countries: [], confidence: 0 };
}

export default function BackfillShipping() {
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [guesses, setGuesses] = useState<Guess[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [status, setStatus] = useState<string>('');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user?.uid || null);
      if (user) {
        const snap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(snap.exists() && snap.data()?.isAdmin === true);
      }
      setCheckingAdmin(false);
    });
    return () => unsub();
  }, []);

  const scan = async () => {
    setLoading(true);
    setError(null);
    setStatus('');
    try {
      const snap = await getDocs(collection(db, 'users'));
      const rows: Guess[] = [];
      for (const d of snap.docs) {
        const data = d.data();
        const legacyLocation: string = (data.location || data.profile?.location || '').toString();
        const legacyShipsTo: string = (data.shipsTo || data.profile?.shipsTo || '').toString();

        const alreadyStructured =
          typeof data.shipsFromCountry === 'string' && data.shipsFromCountry.trim() !== '';

        // Skip fully-populated docs entirely.
        if (alreadyStructured && (data.shipsToWorldwide === true || Array.isArray(data.shipsToCountries)) && data.currency) continue;

        if (!legacyLocation && !legacyShipsTo && !alreadyStructured) continue;

        const fromGuess = guessShipsFromCountry(legacyLocation);
        const homeCode = data.shipsFromCountry || fromGuess.code;
        const toGuess = guessShipsTo(legacyShipsTo, homeCode);
        const currencyGuess = data.currency || (homeCode ? defaultCurrencyForCountry(homeCode) : undefined);

        const confidence = Math.min(3, Math.max(fromGuess.confidence, toGuess.confidence));

        rows.push({
          uid: d.id,
          displayName: data.displayName || data.username || d.id,
          legacyLocation,
          legacyShipsTo,
          currentStructured: {
            shipsFromCountry: data.shipsFromCountry,
            shipsToWorldwide: data.shipsToWorldwide,
            shipsToCountriesCount: Array.isArray(data.shipsToCountries) ? data.shipsToCountries.length : 0,
            currency: data.currency,
          },
          guessed: {
            shipsFromCountry: alreadyStructured ? undefined : fromGuess.code,
            currency: data.currency ? undefined : currencyGuess,
            shipsToWorldwide: data.shipsToWorldwide === true ? undefined : (toGuess.worldwide ? true : undefined),
            shipsToCountries: Array.isArray(data.shipsToCountries) && data.shipsToCountries.length > 0 ? undefined : (toGuess.countries.length > 0 ? toGuess.countries : undefined),
          },
          confidence,
          commit: confidence >= 2,
        });
      }
      // Sort: highest confidence first, then by display name.
      rows.sort((a, b) => b.confidence - a.confidence || a.displayName.localeCompare(b.displayName));
      setGuesses(rows);
      setStatus(`Scan complete. ${rows.length} users to review (${rows.filter((r) => r.commit).length} pre-checked for commit).`);
    } catch (err) {
      setError(`Scan failed: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    setError(null);
    setStatus('');
    try {
      const rowsToCommit = guesses.filter((g) => g.commit);
      let written = 0;
      let batch = writeBatch(db);
      let ops = 0;
      for (const row of rowsToCommit) {
        const update: Record<string, unknown> = {};
        if (row.guessed.shipsFromCountry) update.shipsFromCountry = row.guessed.shipsFromCountry;
        if (row.guessed.currency) update.currency = row.guessed.currency;
        if (row.guessed.shipsToWorldwide) update.shipsToWorldwide = true;
        if (row.guessed.shipsToCountries) update.shipsToCountries = row.guessed.shipsToCountries;
        if (Object.keys(update).length === 0) continue;
        batch.update(doc(db, 'users', row.uid), update);
        ops++;
        if (ops >= BATCH_LIMIT) {
          await batch.commit();
          written += ops;
          batch = writeBatch(db);
          ops = 0;
        }
      }
      if (ops > 0) {
        await batch.commit();
        written += ops;
      }
      setStatus(`Committed structured fields for ${written} users. Their profiles are now on the new schema; their next Marketplace visit uses the country filter default.`);
      setGuesses([]);
    } catch (err) {
      setError(`Commit failed: ${(err as Error).message}`);
    } finally {
      setCommitting(false);
    }
  };

  const toggleCommit = (uid: string) => {
    setGuesses((prev) =>
      prev.map((g) => (g.uid === uid ? { ...g, commit: !g.commit } : g))
    );
  };

  const countryName = (code?: string) =>
    code ? SUPPORTED_COUNTRIES.find((c) => c.code === code)?.name ?? code : '';

  if (checkingAdmin) {
    return <div className="max-w-3xl mx-auto p-8 text-glass-secondary">Checking access…</div>;
  }
  if (!currentUser) {
    return <div className="max-w-3xl mx-auto p-8 text-glass-secondary">Please sign in.</div>;
  }
  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto p-8 text-glass-secondary">Admin only.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-glass-muted hover:text-glass mb-4">
        <ArrowLeft className="w-4 h-4" /> Admin
      </Link>
      <h1 className="text-2xl font-bold text-glass mb-2 inline-flex items-center gap-2">
        <RefreshCw className="w-6 h-6" /> Backfill shipping + currency
      </h1>
      <p className="text-glass-secondary text-sm mb-4">
        One-off migration for users who set their shipping info before the structured schema landed. Scans <code>location</code> + <code>shipsTo</code> free-text fields and guesses country / currency / ships-to. Rows are pre-checked for commit ONLY when the guess is medium-confidence or better; low-confidence rows show but aren't checked so you can flip them on individually or leave them for the user to fill in themselves.
      </p>

      <div className="flex gap-3 mb-6">
        <button
          onClick={scan}
          disabled={loading || committing}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
        >
          {loading ? 'Scanning…' : 'Scan users'}
        </button>
        {guesses.length > 0 && (
          <button
            onClick={commit}
            disabled={committing}
            className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
          >
            {committing
              ? 'Committing…'
              : `Commit selected (${guesses.filter((g) => g.commit).length})`}
          </button>
        )}
      </div>

      {status && (
        <div className="mb-4 p-3 bg-slate-800/40 border border-slate-600 rounded text-glass-secondary">
          {status}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-300">
          {error}
        </div>
      )}

      {guesses.length > 0 && (
        <div className="overflow-x-auto border border-slate-600 rounded">
          <table className="w-full text-xs text-glass-secondary">
            <thead className="bg-slate-800/60 text-glass-muted uppercase">
              <tr>
                <th className="p-2 text-center">Commit</th>
                <th className="p-2 text-left">User</th>
                <th className="p-2 text-left">Legacy location</th>
                <th className="p-2 text-left">Legacy ships-to</th>
                <th className="p-2 text-left">Ships from →</th>
                <th className="p-2 text-left">Currency →</th>
                <th className="p-2 text-left">Ships to →</th>
                <th className="p-2 text-center">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {guesses.map((g) => (
                <tr key={g.uid} className="border-t border-slate-700 hover:bg-slate-800/40">
                  <td className="p-2 text-center">
                    <input type="checkbox" checked={g.commit} onChange={() => toggleCommit(g.uid)} />
                  </td>
                  <td className="p-2 text-glass whitespace-nowrap">{g.displayName}</td>
                  <td className="p-2 max-w-[16ch] truncate" title={g.legacyLocation}>{g.legacyLocation || <span className="text-glass-muted">—</span>}</td>
                  <td className="p-2 max-w-[24ch] truncate" title={g.legacyShipsTo}>{g.legacyShipsTo || <span className="text-glass-muted">—</span>}</td>
                  <td className="p-2">
                    {g.guessed.shipsFromCountry
                      ? countryName(g.guessed.shipsFromCountry)
                      : (g.currentStructured.shipsFromCountry ? <span className="text-glass-muted">already: {countryName(g.currentStructured.shipsFromCountry)}</span> : <span className="text-glass-muted">?</span>)}
                  </td>
                  <td className="p-2">
                    {g.guessed.currency
                      ?? (g.currentStructured.currency ? <span className="text-glass-muted">already: {g.currentStructured.currency}</span> : <span className="text-glass-muted">?</span>)}
                  </td>
                  <td className="p-2 max-w-[22ch]">
                    {g.guessed.shipsToWorldwide
                      ? '🌍 Worldwide'
                      : g.guessed.shipsToCountries
                        ? g.guessed.shipsToCountries.join(', ')
                        : (g.currentStructured.shipsToWorldwide
                            ? <span className="text-glass-muted">already: Worldwide</span>
                            : (g.currentStructured.shipsToCountriesCount && g.currentStructured.shipsToCountriesCount > 0
                                ? <span className="text-glass-muted">already: {g.currentStructured.shipsToCountriesCount} countries</span>
                                : <span className="text-glass-muted">?</span>))}
                  </td>
                  <td className="p-2 text-center">
                    <span className={
                      g.confidence >= 3 ? 'text-emerald-400' :
                      g.confidence === 2 ? 'text-amber-300' :
                      g.confidence === 1 ? 'text-orange-300' :
                      'text-glass-muted'
                    }>
                      {['—', 'low', 'medium', 'strong'][g.confidence]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
